import { useEffect, useState } from "react";
import { Phone, MessageCircle, Send, Check } from "lucide-react";
import { site, whatsappPrefill } from "@/config/site";
import { serviceOptions } from "@/data/services";
import { useEnquiry } from "@/context/EnquiryContext";
import { Section } from "./Section";
import { Reveal } from "./Reveal";

const inputCls =
    "w-full border border-rz-steel bg-transparent px-4 py-3.5 text-base text-rz-white placeholder:text-rz-smoke/60 outline-none transition-colors duration-300 focus:border-rz-red";
const labelCls =
    "mb-2 block font-display text-[10px] uppercase tracking-[0.22em] text-rz-smoke";

const Field = ({ id, label, error, children }) => (
    <div>
        <label htmlFor={id} className={labelCls}>
            {label}
        </label>
        {children}
        {error && (
            <p data-testid={`${id}-error`} className="mt-1.5 text-xs text-rz-red">
                {error}
            </p>
        )}
    </div>
);

export const Enquiry = () => {
    const { service } = useEnquiry();
    const [form, setForm] = useState({
        name: "",
        phone: "",
        email: "",
        make: "",
        model: "",
        service: "",
        message: "",
        company: "",
    });
    const [errors, setErrors] = useState({});
    const [sending, setSending] = useState(false);
    const [sent, setSent] = useState(false);

    useEffect(() => {
        if (service) setForm((f) => ({ ...f, service }));
    }, [service]);

    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const buildMessage = () =>
        [
            "ENQUIRY — REVAUTOZ",
            `Name: ${form.name}`,
            `Phone: ${form.phone}`,
            form.email && `Email: ${form.email}`,
            (form.make || form.model) && `Vehicle: ${form.make} ${form.model}`.trim(),
            form.service && `Service: ${form.service}`,
            form.message && `Message: ${form.message}`,
        ]
            .filter(Boolean)
            .join("\n");

    const submit = (e) => {
        e.preventDefault();
        if (form.company) return;
        const errs = {};
        if (!form.name.trim()) errs.name = "Please tell us your name.";
        if (!/^[+\d][\d\s-]{7,}$/.test(form.phone.trim()))
            errs.phone = "Please enter a valid phone number.";
        if (form.email && !/^\S+@\S+\.\S+$/.test(form.email.trim()))
            errs.email = "That email doesn't look right.";
        setErrors(errs);
        if (Object.keys(errs).length) return;
        setSending(true);
        window.open(whatsappPrefill(buildMessage()), "_blank", "noopener");
        setTimeout(() => {
            setSending(false);
            setSent(true);
        }, 600);
    };

    return (
        <Section id="contact" index="16" label="Enquire" title="Let's talk about your car.">
            <div className="mt-14 grid gap-10 lg:grid-cols-12">
                <Reveal className="lg:col-span-4" delay={0.05}>
                    <p className="max-w-[45ch] text-base leading-relaxed text-rz-smoke">
                        Tell us what you drive and what you want for it. Your enquiry opens in
                        WhatsApp with everything pre-filled — one tap and it reaches the studio.
                    </p>
                    <div className="mt-8 space-y-3 border-l border-rz-steel pl-5">
                        <a
                            data-testid="enquiry-side-call"
                            href={`tel:${site.phone}`}
                            className="block text-base text-rz-white transition-colors hover:text-rz-red"
                        >
                            {site.phoneDisplay}
                        </a>
                        <a
                            data-testid="enquiry-side-instagram"
                            href={site.instagram}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="block text-base text-rz-white transition-colors hover:text-rz-red"
                        >
                            Instagram {site.instagramHandle}
                        </a>
                    </div>
                </Reveal>

                <Reveal className="lg:col-span-8" delay={0.12}>
                    {sent ? (
                        <div
                            data-testid="enquiry-success"
                            className="flex h-full min-h-[320px] flex-col items-start justify-center border border-rz-steel bg-rz-graphite/50 p-8 sm:p-12"
                        >
                            <span className="flex h-12 w-12 items-center justify-center border border-rz-red">
                                <Check size={22} className="text-rz-red" />
                            </span>
                            <h3 className="mt-6 font-display text-2xl font-bold uppercase tracking-wide text-rz-white">
                                Enquiry ready in WhatsApp
                            </h3>
                            <p className="mt-3 max-w-[50ch] text-sm leading-relaxed text-rz-smoke">
                                We opened WhatsApp with your enquiry pre-filled — just press send and
                                it reaches the studio directly. Prefer to talk? Call {site.phoneDisplay}.
                            </p>
                            <div className="mt-8 flex flex-wrap gap-4">
                                <a
                                    data-testid="enquiry-success-whatsapp"
                                    href={whatsappPrefill(buildMessage())}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="bg-rz-red px-7 py-3.5 font-display text-[12px] font-semibold uppercase tracking-[0.18em] text-white transition-colors hover:bg-rz-reddeep"
                                >
                                    Open WhatsApp again
                                </a>
                                <button
                                    data-testid="enquiry-reset"
                                    onClick={() => setSent(false)}
                                    className="border border-rz-steel px-7 py-3.5 font-display text-[12px] font-semibold uppercase tracking-[0.18em] text-rz-smoke transition-colors hover:border-rz-smoke hover:text-rz-white"
                                >
                                    New enquiry
                                </button>
                            </div>
                        </div>
                    ) : (
                        <form
                            data-testid="enquiry-form"
                            onSubmit={submit}
                            noValidate
                            className="border border-rz-steel bg-rz-graphite/50 p-6 sm:p-10"
                        >
                            <div className="grid gap-6 sm:grid-cols-2">
                                <Field id="enquiry-form-input-name" label="Name *" error={errors.name}>
                                    <input
                                        id="enquiry-form-input-name"
                                        data-testid="enquiry-form-input-name"
                                        value={form.name}
                                        onChange={set("name")}
                                        className={inputCls}
                                        autoComplete="name"
                                    />
                                </Field>
                                <Field id="enquiry-form-input-phone" label="Phone *" error={errors.phone}>
                                    <input
                                        id="enquiry-form-input-phone"
                                        data-testid="enquiry-form-input-phone"
                                        value={form.phone}
                                        onChange={set("phone")}
                                        className={inputCls}
                                        inputMode="tel"
                                        autoComplete="tel"
                                    />
                                </Field>
                                <Field id="enquiry-form-input-email" label="Email" error={errors.email}>
                                    <input
                                        id="enquiry-form-input-email"
                                        data-testid="enquiry-form-input-email"
                                        type="email"
                                        value={form.email}
                                        onChange={set("email")}
                                        className={inputCls}
                                        autoComplete="email"
                                    />
                                </Field>
                                <div className="grid grid-cols-2 gap-4">
                                    <Field id="enquiry-form-input-make" label="Vehicle Make">
                                        <input
                                            id="enquiry-form-input-make"
                                            data-testid="enquiry-form-input-vehicle"
                                            value={form.make}
                                            onChange={set("make")}
                                            className={inputCls}
                                            placeholder="e.g. BMW"
                                        />
                                    </Field>
                                    <Field id="enquiry-form-input-model" label="Model">
                                        <input
                                            id="enquiry-form-input-model"
                                            data-testid="enquiry-form-input-model"
                                            value={form.model}
                                            onChange={set("model")}
                                            className={inputCls}
                                            placeholder="e.g. M340i"
                                        />
                                    </Field>
                                </div>
                                <Field id="enquiry-form-select-service" label="Service Required">
                                    <select
                                        id="enquiry-form-select-service"
                                        data-testid="enquiry-form-select-service"
                                        value={form.service}
                                        onChange={set("service")}
                                        className={`${inputCls} appearance-none`}
                                    >
                                        <option value="">Select a service</option>
                                        {serviceOptions.map((o) => (
                                            <option key={o} value={o}>
                                                {o}
                                            </option>
                                        ))}
                                    </select>
                                </Field>
                                <Field id="enquiry-form-textarea-message" label="Message">
                                    <textarea
                                        id="enquiry-form-textarea-message"
                                        data-testid="enquiry-form-textarea-message"
                                        rows={4}
                                        value={form.message}
                                        onChange={set("message")}
                                        className={`${inputCls} resize-y`}
                                    />
                                </Field>
                            </div>
                            <input
                                type="text"
                                name="company"
                                value={form.company}
                                onChange={set("company")}
                                tabIndex={-1}
                                autoComplete="off"
                                aria-hidden="true"
                                className="absolute left-[-9999px] h-0 w-0 opacity-0"
                            />
                            <div className="mt-8 flex flex-wrap gap-4">
                                <button
                                    type="submit"
                                    data-testid="enquiry-form-submit-button"
                                    disabled={sending}
                                    className="btn-sweep inline-flex items-center gap-2 bg-rz-red px-8 py-4 font-display text-[12px] font-semibold uppercase tracking-[0.18em] text-white transition-colors duration-300 hover:bg-rz-reddeep disabled:opacity-60"
                                >
                                    <Send size={14} /> {sending ? "Preparing…" : "Send Enquiry"}
                                </button>
                                <a
                                    data-testid="enquiry-form-whatsapp-button"
                                    href={site.whatsappLink}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center gap-2 border border-rz-white/40 px-8 py-4 font-display text-[12px] font-semibold uppercase tracking-[0.18em] text-rz-white transition-colors duration-300 hover:border-rz-white"
                                >
                                    <MessageCircle size={14} /> WhatsApp
                                </a>
                                <a
                                    data-testid="enquiry-form-call-button"
                                    href={`tel:${site.phone}`}
                                    className="inline-flex items-center gap-2 border border-rz-white/40 px-8 py-4 font-display text-[12px] font-semibold uppercase tracking-[0.18em] text-rz-white transition-colors duration-300 hover:border-rz-white"
                                >
                                    <Phone size={14} /> Call
                                </a>
                            </div>
                        </form>
                    )}
                </Reveal>
            </div>
        </Section>
    );
};
